import {
  Input, Select, Row, Col, Radio, Typography,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FormHeaderLabel from '../../../components/Form/HeaderLabel';
import AlumlyButton from '../../../components/AlumlyButton';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import { ValidationRuleEnum } from '../../../enums/validationRule';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import usePermission from '../../../hooks/usePermission';
import { PermissionEnum } from '../../../enums/permission';

export interface RoleT {
  id: string;
  group: string;
  code: string;
  displayName: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontSize: '0.875rem',
};
const errorSpanStyle: React.CSSProperties = {
  position: 'absolute', bottom: -20, left: 0, color: 'red', fontSize: '0.875rem',
};

const FormEditBoUser: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { checkPermission } = usePermission();

  const [roleOptions, setRoleOptions] = useState<RoleT[]>([]);
  const [user, setUser] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    roles: [],
    isActive: true,
  });
  const [status, setStatus] = useState(true);

  const {
    control, setError, formState: { errors }, handleSubmit, setValue,
  } = useForm({
    defaultValues: {
      password: '',
      firstName: '',
      lastName: '',
      roles: [
        {
          id: '',
          group: '',
          code: '',
          displayName: '',
        },
      ],
      resError: '',
    },
  });
  const handleChangeStatus = (newStatus: boolean): void => {
    setStatus(newStatus);
  };
  const { service: getBoUserService } = useServices(`${ServiceNameEnum.boUser.get}`);
  const getUser = useCallback(async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getBoUserService.get({ params });
    if (data) {
      setUser(data);
    } else if (error) {
      navigate(RouteNameEnum.boUser.index);
    }
  }, [getBoUserService, navigate]);

  const { service: getAllBoRoleService } = useServices(ServiceNameEnum.boRole.index);
  const getRoles = async (): Promise<void> => {
    const { data, error } = await getAllBoRoleService.get();
    if (data) {
      setRoleOptions(data);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
  };

  const { service: editBoUserService } = useServices(`${ServiceNameEnum.boUser.edit}`);
  const onSubmit = async (formData: any): Promise<void> => {
    const {
      password,
      firstName,
      lastName,
      roles,
    } = formData;
    const params = { id };
    const { data, error } = await editBoUserService.put({
      password,
      firstName,
      lastName,
      roleIds: (roles as RoleT[] || []).map((r) => r.id),
      isActive: status,
    }, { params });
    if (data) {
      toast.success('Successfully edited user');
      navigate(RouteNameEnum.boUser.index);
    } else if (error) {
      const errorMessage = error?.response.data.error.errors[0].message;
      setError('resError', {
        type: 'manual',
        message: errorMessage,
      });
    }
  };

  const onChangeRole = (roles: RoleT[]): void => {
    setValue('roles', roles);
  };

  const setUserData = (): void => {
    setValue('firstName', user.firstName);
    setValue('lastName', user.lastName);
    setStatus(user.isActive);
    if (user.roles) {
      const roles = user.roles.map((role: RoleT) => ({
        id: role.id,
        group: role.group,
        code: role.code,
        displayName: role.displayName,
      }));
      onChangeRole(roles);
    }
  };

  const loadAllData = async (): Promise<void> => {
    await getRoles();
    await getUser();
  };

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    setUserData();
  }, [user]);

  const roleError = !!errors?.roles && !(errors?.roles as any)?.ref?.value?.length;

  return (

    <div style={{ paddingTop: 64, justifyItems: 'left' }}>
      <FormHeaderLabel label="Personal info" />
      { checkPermission([PermissionEnum.code.adminEditStatus])
      && (
      <div style={{ marginRight: 24, paddingTop: 24 }}>
        <span style={labelStyle}>Status</span>
        <Radio.Group
          value={status}
          onChange={(e) => handleChangeStatus(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value style={{ height: 48, lineHeight: '46px' }}>Active</Radio.Button>
          <Radio.Button value={false} style={{ height: 48, lineHeight: '46px' }}>Inactive</Radio.Button>
        </Radio.Group>
      </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Row gutter={[16, 16]} justify="space-between">

          { checkPermission([PermissionEnum.code.adminEditDetail])
          && (
          <>
            <Col xs={24} md={11} style={{ marginTop: 24 }}>
              <span style={labelStyle}>Email</span>
              <Input
                type="email"
                size="large"
                disabled
                placeholder="Enter username here"
                value={user.username}
              />
            </Col>
            <Col xs={24} md={11} style={{ marginTop: 24, position: 'relative' }}>
              <span style={labelStyle}>Password</span>
              <Controller
                name="password"
                control={control}
                rules={{
                  minLength: ValidationRuleEnum.password.minLength,
                  pattern: ValidationRuleEnum.password.pattern,
                }}
                render={({ field, fieldState }) => (
                  <Input.Password
                    size="large"
                    placeholder="Enter password"
                    status={fieldState.error ? 'error' : ''}
                    {...field}
                  />
                )}
              />
              {errors.password && (
              <Typography.Text style={errorSpanStyle}>
                { errors.password?.message }
              </Typography.Text>
              )}
            </Col>
            <Col xs={24} md={11} style={{ marginTop: 24 }}>
              <span style={labelStyle}>First name</span>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    size="large"
                    placeholder="Enter first name here"
                    {...field}
                  />
                )}
              />
            </Col>
            <Col xs={24} md={11} style={{ marginTop: 24 }}>
              <span style={labelStyle}>Last name</span>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    size="large"
                    placeholder="Enter last name here"
                    {...field}
                  />
                )}
              />
            </Col>
          </>
          )}
          { checkPermission([PermissionEnum.code.adminEditRole])
          && (
          <Col xs={24} md={11} style={{ position: 'relative' }}>
            <div style={{ display: 'flex', marginTop: 24 }}>
              <span style={labelStyle}>Role</span>
              <span style={{ ...labelStyle, color: 'red' }}>*</span>
            </div>
            <Controller
              name="roles"
              control={control}
              rules={{ required: ValidationRuleEnum.role.required.message }}
              render={({ field: { value } }) => (
                <Select
                  mode="multiple"
                  size="large"
                  style={{ width: '100%' }}
                  status={roleError ? 'error' : ''}
                  value={(value as RoleT[] || []).map((r) => r.id)}
                  onChange={(_, option) => {
                    const opts = Array.isArray(option) ? option : [option];
                    onChangeRole(opts.map((o: any) => o.role));
                  }}
                  options={roleOptions.map((option) => ({
                    value: option.id,
                    label: option.displayName ?? '-',
                    role: option,
                  }))}
                />
              )}
            />
            {roleError && (
            <Typography.Text style={errorSpanStyle}>
              { ValidationRuleEnum.role.required.message }
            </Typography.Text>
            )}
          </Col>
          )}
          <Col xs={24} style={{ position: 'relative' }}>
            {errors.resError && (
              <Typography.Text style={{ color: 'red', fontSize: '0.875rem' }}>{ errors?.resError.message}</Typography.Text>
            )}
          </Col>
          <Col xs={24} style={{ marginTop: 64, display: 'flex', justifyContent: 'center' }}>
            <div style={{ marginRight: 32 }}>
              <AlumlyButton
                label="rxvWhite"
                width={100}
                onClick={() =>
                  navigate(RouteNameEnum.boUser.index)}
              >
                Cancel
              </AlumlyButton>
            </div>
            <AlumlyButton label="rxv" type="submit" width={100}>Save</AlumlyButton>
          </Col>
        </Row>
      </form>
    </div>
  );
};
export default FormEditBoUser;
